// Shared event domain types — single source of truth for the app (src/) and Cloud Functions (functions/).
// This module MUST stay self-contained (no imports) so it compiles cleanly under both the app's
// `bundler` resolution and functions' `nodenext` resolution. See /shared/tsconfig.json.
//
// Originally generated by Firefoo v1.5.11 on 2023-11-07; relocated here from src/types/event.ts.

// --- Act rate categories (moved from constants/optionLists.ts) ---
export const actRateCategories = ['Ensemble', 'Main Band', 'Synergy'] as const;
export type ActRateCategory = (typeof actRateCategories)[number];

// --- Act / entertainment type (moved from src/types/config.ts) ---
export interface EntType {
  // AKA Act
  id: string;
  name: string;
  color: any;
  writeIn?: boolean;
  tags?: string[];
  thumbnailUrl?: string;
  driveFolderId?: string;
  actRateCategory?: ActRateCategory;
}

// --- Contract addressee method (moved from pages/.../ContractAddressMethodEnums.ts) ---
// Member order is significant — these are positional numeric enum values.
export enum AddressMethod {
  ClientNamesOnly,
  ClientCompanyAndClientName,
  ClientNamesAndPlannerName,
  ClientNamesAndPlannerCompany,
  PlannerNameOnly,
  PlannerCompanyOnly,
  PlannerCompanyAndPlannerName
}

// --- Event status + travel options (moved from constants/optionLists.ts) ---
export const eventStatuses = [
  'lead',
  'quote sent',
  'pending',
  'booked',
  'sub-event',
  'dead',
  'guarantee'
] as const;
export type EventStatus = (typeof eventStatuses)[number];

export const travelOptions = [
  '30 min from office',
  '1.5 hours from office',
  '3 hours from office',
  'Flight',
  'Yes, but unknown'
] as const;
export type TravelOption = (typeof travelOptions)[number];

export interface ContactQuickObject {
  name: string;
  id: string;
}

export interface DetailsValues {
  staticDetailsFields: StaticDetailsFields;
  flexDetailsFields: FlexDetailsField[];
  clientFriendlyTitle: string;
  clAlsoInterestedIn: string;
  dateStart: Date | null;
  links: Link[];
}

export interface AttireByTag {
  tagId: string;
  tagName: string;
  attire: string | null;
}

export interface StaticDetailsFields {
  attire?: string;
  attireByContactTag?: AttireByTag[];
  greenRoom?: string;
  greenRoomLocation?: string;
  loadInInfo?: string;
  onSiteContact?: string;
  venueContact?: string;
  plannerContact?: string;
  parkingDetails?: string;
  performanceLocations?: string;
  producerNotes?: string;
  producerToBring?: string;
  soundContact?: string;
  soundNotesInternal?: string;
  soundNotes?: string;
  vendorMealCount?: string;
  wifi?: string;
  travel?: string;
  internalClientCallOne?: string;
  internalClientCallTwo?: string;
  internalBandStands?: string;
  inputList?: string;
  backlineNotes?: string;
}

export interface ContractBreaks {
  contractTotals?: boolean;
  contractTerms?: boolean;
  contractPaymentInfo?: boolean;
  pageBreakBeforeGroups?: string[];
}

export interface InvoiceBreaks {
  invoiceTotals?: boolean;
  invoicePaymentInfo?: boolean;
  pageBreakBeforeGroups?: string[];
}

export interface QuoteBreaks {
  quoteTotals?: boolean;
  pageBreakBeforeGroups?: string[];
}

export interface Link {
  title: string;
  url: string;
  color: string;
}

export interface Event {
  attire?: string;
  avTech?: string;
  bookedDateActual?: Date | null;
  bandleader?: ContactQuickObject;
  breakPDFSections?: boolean;
  // Keys of jobsheet headings (document sections, segments, and General/Timeline subgroups) that
  // should start on a new page. Replaces the all-or-nothing breakPDFSections toggle.
  pageBreakBeforeJobsheet?: string[];
  breakContractSections?: ContractBreaks;
  breakInvoiceSections?: InvoiceBreaks;
  breakQuoteSections?: QuoteBreaks;
  contractVersion?: string;
  musicalDirector?: ContactQuickObject;
  soundEngineer?: ContactQuickObject;
  strings?: ContactQuickObject;
  horns?: ContactQuickObject;
  percussion?: ContactQuickObject;
  careOf?: string;
  clAlsoInterestedIn?: string;
  clDateBooked: Date | null; // not in use and can be removed
  clTypeofEvent?: string; // Wedding, Corporate, etc.
  clTypeofEventDetailed?: string;
  clients: Client[];
  clientIds?: string[];
  clientFriendlyTitle?: string;
  complexityRating?: number;
  contractAddressMethod?: AddressMethod;
  contractAddressTo?: string;
  qualityRating?: number;
  createdBy: string;
  createdDate: Date | null;
  createdTime: string;
  dateStart: Date | null;
  discount?: number;
  id: string;
  eventPrime?: number | boolean;
  eventTitle: string;
  eventType?: EntType; // aka the band or type of performance, NOT Booked, Lead, etc.
  financialNotes?: string;
  flexDetailsFields?: FlexDetailsField[];
  googleCalEventId?: string;
  googleCalLastSync?: Date | null;
  jobCheatSheet?: string;
  staticDetailsFields: StaticDetailsFields;
  jobClosed?: boolean;
  jobFileId?: string;
  jobFolderId?: string;
  protectedFolderId?: string;
  jobLeadSource?: string;
  lastPortalEdit?: Date;
  links?: Link[];
  loadInInfo?: string;
  modifiedBy: string;
  modifiedDate: Date | null;
  modifiedTime: string;
  mondayBoardId?: number;
  mondayBoardUrl?: string;
  nickName?: string;
  otmContact?: number;
  paidFlag?: boolean;
  /**
   * Active recurring (pre-approved) managed-payment method id for this event.
   * Denormalized from `securePaymentMethods` by the managed-payment functions so
   * the client can flag pre-approved invoices without reading that Admin-only
   * collection. Cleared when the method is purged.
   */
  recurringPaymentMethodId?: string | null;
  preProdLead?: ContactQuickObject;
  producer?: Producer;
  portalTemplate: string;
  relSubEventId?: string; // should be relParentEventId
  referral?: string[];
  salesLead?: SalesLead;
  status: (typeof eventStatuses)[number]; // Lead, Booked, etc.
  timeEnd?: Date | null;
  timeStart?: Date | null;
  terms?: string;
  taskProgress?: { done: number; total: number } | null;
  techConfirmed?: boolean;
  travel: TravelOption | 1 | 0 | '' | null;
  venue?: Venue;
  wardrobeNeeded?: boolean;
}

export interface TimelineItem {
  label?: string;
  time: Date | null;
}

export interface ClientResponse {
  /** The client's answer for the field — text/boolean/etc. Same meaning for every field type. */
  value: PortalValue;
  /**
   * Details v2: a client's proposed timeline times for a `timeline:true` field. Additive + optional,
   * so legacy responses (which never carry them) are unaffected and never error. The canonical
   * `field.start`/`field.end` stay staff-owned until accepted; these hold the client's proposal.
   * `start`/`end` keys are whitelisted no-offset in useProcessTimeStamps wherever they appear.
   */
  start?: Date | null;
  end?: Date | null;
  timestamp?: Date | null;
}

export type PortalValue = string | boolean | TimelineItem[] | { time: Date | null } | null;

export interface FlexDetailsField {
  id?: string;
  title: string;
  value: PortalValue;
  timestamp: Date | null;
  desc: string;
  onDashboard: boolean;
  onJobsheet: boolean;
  onPortal: boolean;
  includeTime: boolean;
  showInfoText: boolean;
  clientResponseHistory: ClientResponse[];
  clientResponse: ClientResponse;
  type: 'standard' | 'richText' | 'boolean' | 'image' | 'timeline' | 'section';
  finalized?: boolean;
  finalizedBy?: string;
  finalizedAt?: Date | null;
  hideFromFlex?: boolean;
  onTasks?: boolean;
  // --- Details v2 (Segments tab) — additive, optional; ignored by legacy reads/writes ---
  /** Marks this field as a timeline (grid) row rather than a header/general field. */
  timeline?: boolean;
  /** Timeline start time (picker wall-clock — see useProcessTimeStamps no-offset whitelist). */
  start?: Date | null;
  /** Timeline end time (picker wall-clock). */
  end?: Date | null;
  /** Link to the owning Segment doc (`segments` collection). The new grouping relationship. */
  segmentId?: string;
  /**
   * Tag IDs from the `All Tags › Talent` branch of the tag tree (`config/tagSets`). Multiple
   * per row; resolved to names for display via `getNameFromId(id, contactTagObject)`. Additive +
   * backward compatible — legacy reads/writes ignore it.
   */
  tags?: string[];
  /**
   * Image gallery for `type:'image'` fields — the full set of image URLs in display order.
   * Additive + backward compatible: `value` always holds the PRIMARY image (`images[0]`), so
   * legacy single-image consumers (dashboard, jobsheet PDF, GetPreview) keep working unchanged.
   * A legacy image field with only `value` is read as `[value]`.
   */
  images?: string[];
  /**
   * Custom columns, self-contained on the item (no separate registry). Each entry is
   * `{ id, name, value }`: `id` a stable column id, `name` the editable header, `value` sanitized
   * rich-text HTML (same shape as a richText `value`). A segment's column set is derived from the
   * union (by `id`) of these entries across its fields. Additive + backward compatible: legacy
   * fields have none; a missing entry reads as ''. External-facing: rendered (grouped under their
   * field) on the talent dashboard and jobsheet PDF, gated by the field's `onDashboard`/`onJobsheet`.
   * A cell can also be delegated to the client portal per-cell (`onPortal`) — see `CustomColumnValue`.
   */
  customCols?: CustomColumnValue[];
}

/** One custom-column cell on a flex item — see `FlexDetailsField.customCols`. */
export interface CustomColumnValue {
  /** Stable column id; also the DataGrid column `field`. Matches the column across rows. */
  id: string;
  /** Editable column header. Renamed in place; values stay put because they key on `id`. */
  name: string;
  /** Sanitized rich-text HTML (same shape as a richText `value`). */
  value: string;
  /**
   * Per-cell portal delegation — the custom-column analog of `FlexDetailsField.onPortal`, but at cell
   * grain. When true, the client sees this cell as an indented sub-question and answers into
   * `clientResponse`. Additive + optional: legacy/undelegated cells omit it (read as not-on-portal).
   * Custom cells are always rich-text, so `clientResponse` only ever uses `value`/`timestamp` (no
   * timeline start/end).
   */
  onPortal?: boolean;
  /** Staff cell edit time — the cell analog of `FlexDetailsField.timestamp`; drives the attention
   *  comparison against `clientResponse.timestamp`. Stamped when staff edits or accepts the value. */
  timestamp?: Date | null;
  /** The client's answer to this delegated cell (value + timestamp; times unused). */
  clientResponse?: ClientResponse;
  /** Prior client answers, newest pushed on change (mirrors `FlexDetailsField.clientResponseHistory`). */
  clientResponseHistory?: ClientResponse[];
}

/**
 * Details v2 container (the `segments` collection — one doc per segment).
 * Holds segment-level metadata ONLY — never the flex fields themselves, which stay in
 * `event.flexDetailsFields` and link back via `FlexDetailsField.segmentId`.
 */
export interface Segment {
  id: string;
  relEventId: string;
  title: string;
  /** Authored segment color (raw hex from the palette picker). Adjusted display-only per theme mode
   *  (lightened on dark surfaces, darkened on light) — never persisted adjusted. Cleared to null. */
  color?: string | null;
  /** Authored segment time window (wall-clock, timezone-naive). When present, overrides the derived
   *  earliest-start / latest-end window from the segment's timeline items. Written ONLY on explicit
   *  user confirm (never a derived auto-persist) — see SegmentHeaderBar. Cleared to null to fall back
   *  to the derived window. */
  timeStart?: Date | null;
  timeEnd?: Date | null;
  finalized?: boolean;
  finalizedBy?: string;
  finalizedAt?: Date | null;
  onTasks?: boolean;
  /** Talent roster: when true, this segment gets a per-gig assignment check column and appears in
   *  the bulk assign/remove segment menus. Opt-in — unset/false means not assignable. */
  rosterAssignable?: boolean;
  order: number;
  modified?: Date | null;
  modifiedBy?: string;
}

export interface Client {
  city?: string;
  email1?: string;
  id: string;
  name: string;
  phone?: string;
  planner?: boolean;
  state?: string;
  street?: string;
  zip?: number | string;
  /** @deprecated Use includeOnContract instead */
  primary?: boolean;
  /** Whether this client's name should appear on the contract addressee line */
  includeOnContract?: boolean;
  /** @deprecated Use billingContact instead */
  onContract?: boolean;
  /** Whether this client's company and address should be used for billing/mailing */
  billingContact?: boolean;
  /** Contract-only: include this client on the Cc line of the contract email. */
  contractCc?: boolean;
  portal?: boolean;
  description?: string;
  [key: string]: any;
}

export interface Producer {
  id: string;
  name: string;
  writeIn?: boolean;
}

export interface SalesLead {
  id: string;
  name: string;
}

export interface Venue {
  id: string;
  name: string;
  description?: string;
  writeInAddress?: any;
  writeIn?: boolean;
  [key: string]: any;
}

export const clientInit: Client = {
  id: '',
  name: '',
  includeOnContract: false,
  billingContact: false
};

export const venueInit: Venue = {
  id: '',
  name: '',
  desc: '',
  writeInAddress: {
    add1City: '',
    add1State: '',
    add1Street: '',
    add1StreetB: '',
    add1Zip: '',
    desc: ''
  }
};

export const staticDetailsFieldsInit: StaticDetailsFields = {
  attire: '',
  attireByContactTag: [],
  greenRoomLocation: '',
  loadInInfo: '',
  onSiteContact: '',
  venueContact: '',
  plannerContact: '',
  producerToBring: '',
  parkingDetails: '',
  performanceLocations: '',
  vendorMealCount: '',
  producerNotes: '',
  wifi: '',
  travel: '',
  internalClientCallOne: '',
  internalClientCallTwo: '',
  internalBandStands: '',
  soundContact: '',
  soundNotesInternal: '',
  soundNotes: '',
  inputList: '',
  backlineNotes: ''
};

export const eventInit: Event = {
  attire: '',
  avTech: '',
  bookedDateActual: null,
  bandleader: {
    id: '',
    name: ''
  },
  musicalDirector: {
    id: '',
    name: ''
  },
  soundEngineer: {
    id: '',
    name: ''
  },
  strings: {
    id: '',
    name: ''
  },
  horns: {
    id: '',
    name: ''
  },
  percussion: {
    id: '',
    name: ''
  },
  careOf: '',
  clAlsoInterestedIn: '',
  clDateBooked: null,
  clTypeofEvent: '',
  clTypeofEventDetailed: '',
  clients: [],
  complexityRating: 0,
  qualityRating: 0,
  createdBy: '',
  createdDate: null,
  createdTime: '',
  contractAddressMethod: AddressMethod.ClientNamesOnly,
  contractAddressTo: '',
  dateStart: null,
  discount: 0,
  eventPrime: 0,
  eventTitle: '',
  eventType: {
    id: '',
    name: '',
    color: ''
  },
  financialNotes: '',
  flexDetailsFields: [
    {
      title: '',
      value: '',
      timestamp: null,
      desc: '',
      onDashboard: true,
      onJobsheet: true,
      onPortal: false,
      includeTime: false,
      showInfoText: false,
      type: 'standard',
      clientResponseHistory: [],
      clientResponse: { value: '', timestamp: null }
    }
  ],
  googleCalEventId: '',
  id: '',
  jobCheatSheet: '',
  jobClosed: false,
  jobFileId: '',
  jobFolderId: '',
  jobLeadSource: '',
  staticDetailsFields: staticDetailsFieldsInit,
  loadInInfo: '',
  links: [],
  modifiedBy: '',
  modifiedDate: null,
  modifiedTime: '',
  mondayBoardId: 0,
  mondayBoardUrl: '',
  nickName: '',
  otmContact: 0,
  paidFlag: false,
  portalTemplate: '',
  preProdLead: {
    id: '',
    name: ''
  },
  producer: {
    id: '',
    name: ''
  },
  referral: [],
  salesLead: {
    id: '',
    name: ''
  },
  status: 'lead',
  taskProgress: null,
  timeEnd: null,
  timeStart: null,
  terms: '',
  techConfirmed: false,
  travel: 0,
  venue: venueInit,
  wardrobeNeeded: false
};
